import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Use current domain for API calls - same as api.js
// This ensures production deployments work correctly
const API_URL = window.location.origin;

// Create axios instance with credentials for auth
const authAxios = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Important: sends httpOnly cookies
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const logout = useCallback(async () => {
    try {
      // Call logout endpoint to clear httpOnly cookie
      await authAxios.post('/api/auth/logout');
    } catch (error) {
      // Ignore errors during logout
    }
    setUser(null);
    setMustChangePassword(false);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      // Cookie is sent automatically with withCredentials
      const response = await authAxios.get('/api/auth/me');
      setUser(response.data);
      setMustChangePassword(response.data.must_change_password || false);
    } catch (error) {
      // Not authenticated or token expired - silently ignore
      // This is expected behavior for unauthenticated users
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await authAxios.post('/api/auth/login', { email, password });
    const { user: userData, must_change_password } = response.data;
    // Cookie is automatically set by browser from response
    setUser(userData);
    setMustChangePassword(must_change_password || false);
    return { ...userData, must_change_password };
  };

  const register = async (userData) => {
    const response = await authAxios.post('/api/auth/register', userData);
    const { user: newUser } = response.data;
    // Cookie is automatically set by browser from response
    setUser(newUser);
    setMustChangePassword(false);
    return newUser;
  };

  const clearPasswordChangeRequired = () => {
    setMustChangePassword(false);
    if (user) {
      setUser({ ...user, must_change_password: false });
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    mustChangePassword,
    clearPasswordChangeRequired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
